import { useEffect } from 'react';
import { Editor } from '@tiptap/react';

export default function useEditorShortcuts(editor: Editor | null) {
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when editor is focused
      if (!editor.isFocused) return;
      
      // Check for both macOS (metaKey) and Windows/Linux (ctrlKey)
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const isCmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;
      
      if (isCmdOrCtrl) {
        switch (event.key.toLowerCase()) {
          case 'b':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleBold().run();
            break;
          case 'i':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleItalic().run();
            break;
          case 'u':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleUnderline().run();
            break;
          case 's':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleStrike().run();
            break;
          case 'h':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleHighlight().run();
            break;
          case 'z':
            event.preventDefault();
            event.stopPropagation();
            if (event.shiftKey) {
              editor.chain().focus().redo().run();
            } else {
              editor.chain().focus().undo().run();
            }
            break;
          case 'y':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().redo().run();
            break;
          case 'k': {
            event.preventDefault();
            event.stopPropagation();
            // Trigger link URL modal via custom event
            // The RichTextEditor component will handle the modal
            const linkEvent = new CustomEvent('open-link-url-modal', {
              detail: {
                onConfirm: (url: string) => {
              editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            }
              }
            });
            window.dispatchEvent(linkEvent);
            break;
          }
        }
      }
      
      // Handle additional shortcuts with shift key
      if (isCmdOrCtrl && event.shiftKey) {
        switch (event.key.toLowerCase()) {
          case '7':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleOrderedList().run();
            break;
          case '8':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleBulletList().run();
            break;
          case 'x':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleStrike().run();
            break;
        }
      }

      // Handle alt key combinations for headings
      if (isCmdOrCtrl && event.altKey) {
        switch (event.key) {
          case '1':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleHeading({ level: 1 }).run();
            break;
          case '2':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleHeading({ level: 2 }).run();
            break;
          case '3':
            event.preventDefault();
            event.stopPropagation();
            editor.chain().focus().toggleHeading({ level: 3 }).run();
            break;
        }
      }
    };

    // Add event listener to the document to catch all keyboard events
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [editor]);
} 